import React from 'react';

/**
 * PulseBeacon - Animated issue indicator with expanding radiating ring
 * 
 * Props:
 * @param {string} severity - 'critical' | 'warning' | 'info' | null/undefined (no beacon)
 * @param {number} size - Dot diameter in px (default: 10)
 * @param {object} style - Additional positioning styles
 * @param {boolean} isDarkMode - Theme flag
 */
const PulseBeacon = ({ severity, size = 10, style = {}, isDarkMode = false }) => {
  if (!severity || severity === 'good' || severity === 'none') return null;

  const config = {
    critical: {
      color: '#EF4444',
      glowColor: isDarkMode ? 'rgba(239, 68, 68, 0.6)' : 'rgba(239, 68, 68, 0.4)',
      ringColor: isDarkMode ? 'rgba(239, 68, 68, 0.4)' : 'rgba(239, 68, 68, 0.3)',
      duration: '1.8s',
    },
    warning: {
      color: '#F59E0B',
      glowColor: isDarkMode ? 'rgba(245, 158, 11, 0.5)' : 'rgba(245, 158, 11, 0.35)',
      ringColor: isDarkMode ? 'rgba(245, 158, 11, 0.35)' : 'rgba(245, 158, 11, 0.25)',
      duration: '2.5s',
    },
    info: {
      color: '#3B82F6',
      glowColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.35)',
      ringColor: isDarkMode ? 'rgba(59, 130, 246, 0.35)' : 'rgba(59, 130, 246, 0.25)',
      duration: '3s',
    }
  };

  const c = config[severity] || config.warning;
  const ringMaxSize = size * 3.5;
  const uniqueId = `pulse-${severity}-${size}`;

  return (
    <>
      <style>{`
        @keyframes ${uniqueId}-ping {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          70% {
            transform: scale(${ringMaxSize / size});
            opacity: 0;
          }
          100% {
            transform: scale(${ringMaxSize / size});
            opacity: 0;
          }
        }
        @keyframes ${uniqueId}-breathe {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 ${size * 0.5}px ${c.glowColor}, 0 0 ${size}px ${c.glowColor};
          }
          50% {
            transform: scale(1.2);
            box-shadow: 0 0 ${size * 0.8}px ${c.glowColor}, 0 0 ${size * 1.5}px ${c.glowColor};
          }
        }
      `}</style>
      <div
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          width: `${size}px`,
          height: `${size}px`,
          zIndex: 5,
          ...style
        }}
      >
        {/* Radiating ring */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px solid ${c.ringColor}`,
            animation: `${uniqueId}-ping ${c.duration} cubic-bezier(0, 0, 0.2, 1) infinite`,
          }}
        />
        {/* Second ring offset */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `1.5px solid ${c.ringColor}`,
            animation: `${uniqueId}-ping ${c.duration} cubic-bezier(0, 0, 0.2, 1) infinite`,
            animationDelay: `${parseFloat(c.duration) * 0.4}s`,
          }}
        />
        {/* Core dot */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backgroundColor: c.color,
            animation: `${uniqueId}-breathe ${c.duration} ease-in-out infinite`,
          }}
        />
      </div>
    </>
  );
};

export default PulseBeacon;
